import { tryParseMoney } from '@sitzmark/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Explain,
  Loading,
  Modal,
  Mono,
  More,
  Screen,
  Stat,
} from '../../components/ui.js';
import { EmailField, PhoneField } from '../../components/contact-fields.js';
import { useNavReset } from '../../components/nav-reset.js';
import { useNotice } from '../../components/notice.js';
import { SearchSelect } from '../../components/search-select.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { useSearchTyping } from '../../lib/typing-focus.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { ReceiptDialog } from '../sales/sales-screen.js';
import { customerName } from './customer.js';
import { CustomerConsignment } from './customer-consignment.js';
import { DuplicateNotice, MergeCustomerDialog, useDuplicates } from './duplicates.js';
import { NewCustomerDialog } from './new-customer.js';
import './customers.css';
import { DateInput } from '../../components/date-input.js';

/** How many customers browsing lists before it asks for a name (FB-1127). */
const BROWSE_LIMIT = 100;

/**
 * Customers (SCOPE §9.1).
 *
 * Two panes, because the counter question is rarely one lookup. Search stays
 * put on the left while a profile fills the right, so "no, the other Solberg"
 * costs one press rather than a fresh search (§2.2-3, recognition over recall).
 *
 * The profile answers, in this order: who are they, what have they spent, what
 * did they buy. Every figure carries its derivation (§2.2-10) — a lifetime
 * total nobody can account for is a total that gets re-added on a calculator.
 */

type Profile = Awaited<ReturnType<typeof api.customers.get.query>>;

export function CustomersScreen({
  onReturnStarted,
  initialCustomerId = null,
}: {
  onReturnStarted: () => void;
  /**
   * Somebody handed over from another screen — a name clicked on a sale.
   * Read once, on mount: the screen is unmounted when you navigate away, so
   * arriving again with a different customer re-runs this.
   */
  initialCustomerId?: string | null;
}) {
  const [term, setTerm] = useState('');
  /** Typing anywhere here types into the search, as on the register. */
  const searchRef = useSearchTyping(setTerm);
  const [debounced, setDebounced] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(initialCustomerId);

  /*
   * Whether the results list is still worth the space (client, 2026-08-15).
   *
   * Once somebody is open, the list has done its job — and it was holding a
   * third of the screen in front of the widest table in the product, a purchase
   * history at line grain. So it stands down and the profile takes the full
   * width.
   *
   * **The search box itself never goes.** Hiding the way to look somebody else
   * up would trade one problem for a worse one; what stands down is the list of
   * results nobody is reading. Typing brings it straight back, which is the
   * rule a person would guess (§2.2-3) and needs no button to explain it.
   */
  const [browsing, setBrowsing] = useState(initialCustomerId === null);
  const showProfileFullWidth = !browsing && selectedId !== null;
  const [adding, setAdding] = useState(false);

  // Pressing Customers while a profile is open goes back to browsing
  // (item 31); the search box keeps what was typed.
  useNavReset(() => {
    setSelectedId(null);
    setBrowsing(true);
    setAdding(false);
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 150);
    return () => clearTimeout(timer);
  }, [term]);

  /*
   * The list is there before the first keystroke (§2.2-3).
   *
   * This screen used to hold a blank panel until somebody typed — in the one
   * place a name is most likely to be half-remembered, and while Products has
   * always shown its catalogue straight away. Browsing returns everyone,
   * alphabetically, because that is the only order a list can be scanned in;
   * typing switches to ranked search.
   */
  const results = useQuery({
    queryKey: ['customers', 'search', debounced],
    queryFn: () =>
      api.customers.search.query({ term: debounced, limit: debounced === '' ? BROWSE_LIMIT : 25 }),
  });

  const found = results.data ?? [];

  return (
    <Screen
      title="Customers"
      subtitle="Find someone, see what they have bought, keep their details straight."
      primaryAction={
        <Button variant="primary" onClick={() => setAdding(true)}>
          New customer
        </Button>
      }
    >
      <div className={`customers${showProfileFullWidth ? ' customers--open' : ''}`}>
        <div className="customers__list">
          <input
            ref={searchRef}
            className="search__input"
            placeholder="Search by name, phone or email…"
            value={term}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              setTerm(event.target.value);
              // Typing is the signal that they are looking for somebody else.
              setBrowsing(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && found[0] !== undefined) {
                event.preventDefault();
                setSelectedId(found[0].id);
              }
            }}
          />

          {/*
            The list, only while it is being read. It stands down once somebody
            is open so the profile gets the width, and comes back the moment
            anything is typed — the search box above it never moves.
          */}
          {!browsing ? null : results.isError ? (
            <Callout tone="danger">{errorMessage(results.error)}</Callout>
          ) : results.isPending ? (
            <Loading label={debounced === '' ? 'Loading customers' : 'Searching'} />
          ) : found.length === 0 ? (
            <Card>
              <p className="text-secondary">
                {debounced === ''
                  ? 'No customers yet. They are added at the register, or with New customer above.'
                  : `Nobody matches “${debounced}”.`}
              </p>
            </Card>
          ) : (
            <Card padded={false}>
              <ul className="customer-results customer-results--flush">
                {found.map((person) => (
                  <li key={person.id}>
                    <button
                      type="button"
                      className={`customer-result${person.id === selectedId ? ' customer-result--active' : ''}`}
                      onClick={() => {
                        setSelectedId(person.id);
                        setBrowsing(false);
                      }}
                    >
                      <span className="customer-result__name">
                        {customerName(person)}
                        {person.taxExempt ? <Badge tone="info">Tax exempt</Badge> : null}
                      </span>
                      <span className="customer-result__meta">
                        {[person.phone, person.email].filter((part) => part != null).join(' · ')}
                      </span>
                      <span className="customer-result__seen">
                        {person.lastVisitAt === null
                          ? 'No purchases yet'
                          : `Last in ${new Date(person.lastVisitAt).toLocaleDateString()}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {/*
                The list stops at the first hundred, A to Z; saying so rather
                than stopping silently (FB-1127: a list that ends at Alexander
                reads as wrong).
              */}
              {debounced === '' && found.length >= BROWSE_LIMIT ? (
                <p className="customer-results__more text-secondary">
                  The first {BROWSE_LIMIT}, A to Z. Type a name, phone or email to find anyone else.
                </p>
              ) : null}
            </Card>
          )}
        </div>

        <div className="customers__profile">
          {selectedId === null ? (
            <Card>
              <p className="text-secondary">Pick someone to see their profile and history.</p>
            </Card>
          ) : (
            <CustomerProfile
              customerId={selectedId}
              onReturnStarted={onReturnStarted}
              onClose={() => setSelectedId(null)}
            />
          )}
        </div>
      </div>

      {adding ? (
        <NewCustomerDialog
          onClose={() => setAdding(false)}
          onCreated={(id) => {
            setAdding(false);
            setSelectedId(id);
          }}
        />
      ) : null}
    </Screen>
  );
}

// ---------------------------------------------------------------------------

function CustomerProfile({
  customerId,
  onReturnStarted,
  onClose,
}: {
  customerId: string;
  onReturnStarted: () => void;
  onClose: () => void;
}) {
  const [dialog, setDialog] = useState<'edit' | 'taxExempt' | 'merge' | null>(null);
  const [mergeCandidate, setMergeCandidate] = useState<{ id: string; name: string } | null>(null);

  const profile = useQuery({
    queryKey: ['customers', 'get', customerId],
    queryFn: () => api.customers.get.query({ customerId }),
    // Their credit and history change at the register, on another screen:
    // opening the profile must show the sale that just happened, not the one
    // cached fifteen seconds ago (found by the consignment journey).
    refetchOnMount: 'always',
  });

  // Another record with this person's email or phone (FB-1104) — offered to
  // merge here, where the history that would be split is on show.
  const sameDetails = useDuplicates(
    profile.data?.customer.email ?? '',
    profile.data?.customer.phone ?? '',
    customerId,
  );

  if (profile.isPending) return <Loading label="Loading the profile" />;
  if (profile.isError) return <Callout tone="danger">{errorMessage(profile.error)}</Callout>;

  const { customer, tags, stats, storeCreditCents, storeCreditLedger } = profile.data;

  return (
    <div className="profile">
      <Card
        title={customerName(customer)}
        actions={
          <div className="profile__actions">
            <Button variant="ghost" onClick={() => setDialog('edit')}>
              Edit
            </Button>
            {/* There is always a way out of a screen (§2.2-11). */}
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        }
      >
        <div className="profile__contact">
          {customer.phone != null ? <span>{customer.phone}</span> : null}
          {customer.email != null ? <span>{customer.email}</span> : null}
          {customer.companyName != null ? <span>{customer.companyName}</span> : null}
          {customer.taxExempt ? (
            <Badge tone="info">
              Tax exempt
              {customer.taxExemptReference != null ? ` · ${customer.taxExemptReference}` : ''}
            </Badge>
          ) : null}
          {customer.marketingOptIn ? <Badge tone="neutral">Marketing OK</Badge> : null}
          {!customer.active ? <Badge tone="warning">Inactive</Badge> : null}
        </div>

        {customer.notes != null ? <p className="profile__notes">{customer.notes}</p> : null}

        <TagEditor customerId={customerId} tags={tags} />

        {customer.active ? (
          <DuplicateNotice
            duplicates={sameDetails.duplicates}
            useLabel={`Merge into ${customerName(customer)}`}
            onUse={(id) => {
              const other = sameDetails.duplicates.find((entry) => entry.id === id);
              setMergeCandidate(other === undefined ? null : { id, name: other.name });
              setDialog('merge');
            }}
          />
        ) : null}
      </Card>

      <Card>
        <div className="profile__stats">
          <Stat
            emphasis
            label={
              <>
                Lifetime spend
                <Explain label="lifetime spend">
                  {stats.purchaseCount} purchase{stats.purchaseCount === 1 ? '' : 's'} totalling{' '}
                  {money(stats.netSpendCents + stats.refundedCents)}, less{' '}
                  {money(stats.refundedCents)} returned. Tax included, exactly as it was charged.
                  {stats.beforeShekko.purchaseCount > 0
                    ? ` ${stats.beforeShekko.purchaseCount} of the purchases, ${money(stats.beforeShekko.spendCents)}, were before Shekko, in the old system.`
                    : ''}
                </Explain>
              </>
            }
            value={money(stats.netSpendCents)}
          />
          <Stat label="Purchases" value={stats.purchaseCount} />
          <Stat label="Items bought" value={stats.unitCount} />
          <Stat
            label={
              <>
                Average sale
                <Explain label="average sale">
                  {money(stats.netSpendCents)} across {stats.purchaseCount} purchase
                  {stats.purchaseCount === 1 ? '' : 's'}.
                </Explain>
              </>
            }
            value={stats.purchaseCount === 0 ? '—' : money(stats.averageSaleCents)}
          />
          <Stat
            label="Last in"
            value={
              stats.lastVisitAt === null ? '—' : new Date(stats.lastVisitAt).toLocaleDateString()
            }
          />
          {/*
            Shown only when there is credit to show (§2.2-13) — a $0.00 row on
            every profile is noise. It is spendable at the register now, so this
            is a figure the counter acts on rather than a note to somebody.
          */}
          {storeCreditCents !== 0 ? (
            <Stat
              label={
                <>
                  Store credit
                  {/*
                    §2.2-10: a derived figure explains itself. This one is a sum
                    of a ledger, and "where did $40 come from?" is the question
                    it raises the moment credit can be issued by hand.
                  */}
                  <Explain label="store credit">
                    {storeCreditLedger.length} movement
                    {storeCreditLedger.length === 1 ? '' : 's'} on the account, most recent first,
                    below.
                  </Explain>
                </>
              }
              tone="warning"
              value={money(storeCreditCents)}
            />
          ) : null}
        </div>

        <More label="Store credit">
          <StoreCreditPanel
            customerId={customerId}
            customerName={customerName(customer)}
            balanceCents={storeCreditCents}
            ledger={storeCreditLedger}
          />
        </More>

        <More label="Tax exemption, status and duplicates">
          <div className="profile__admin">
            <p className="text-secondary">
              {customer.taxExempt
                ? 'Sales to this customer are not taxed. A manager can remove the exemption.'
                : 'Marking a customer tax-exempt zeroes the tax on every sale they make. A manager approves it and the certificate reference is kept on the record.'}
            </p>
            <Button variant="secondary" onClick={() => setDialog('taxExempt')}>
              {customer.taxExempt ? 'Remove tax exemption' : 'Mark tax exempt'}
            </Button>
          </div>
          {customer.active ? (
            <div className="profile__admin">
              <p className="text-secondary">
                Two records of one person split their history, credit and web orders. Merging moves
                everything on the other record into this one.
              </p>
              <Button
                variant="secondary"
                onClick={() => {
                  setMergeCandidate(null);
                  setDialog('merge');
                }}
              >
                Merge another record into this one
              </Button>
            </div>
          ) : null}
        </More>
      </Card>

      <HeldForCustomer customerId={customerId} onReturnStarted={onReturnStarted} />
      <PastLayaways customerId={customerId} onReturnStarted={onReturnStarted} />
      <CustomerRepairs customerId={customerId} />
      <CustomerConsignment customerId={customerId} />

      <PurchaseHistory customerId={customerId} onReturnStarted={onReturnStarted} />
      <PastPurchases customerId={customerId} />

      {dialog === 'edit' ? (
        <EditCustomerDialog customer={customer} onClose={() => setDialog(null)} />
      ) : null}

      {dialog === 'merge' ? (
        <MergeCustomerDialog
          customerId={customerId}
          customerName={customerName(customer)}
          candidate={mergeCandidate}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'taxExempt' ? (
        <TaxExemptDialog
          customerId={customerId}
          customerName={customerName(customer)}
          currentlyExempt={customer.taxExempt}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * What the shop is holding for this person (SCOPE §8.3, §8.4).
 *
 * Two things that were invisible everywhere except the transaction that created
 * them: **layaways**, which are goods the shop is keeping and money it has taken
 * against them, and **gift cards**, which are money the shop owes. Both are
 * asked about at the counter by a person standing in front of you, and until now
 * the only way to answer either was to find the original sale.
 *
 * Deliberately built out of the endpoints that already existed — `layaways` and
 * `giftCards` both take a `customerId`, and a card has carried its owner since
 * it was issued. There is no new schema and no new query behind this screen;
 * what was missing was a place to put the answer.
 *
 * Absent entirely when there is nothing held, rather than two empty tables
 * (§2.2-13).
 */
function HeldForCustomer({
  customerId,
  onReturnStarted,
}: {
  customerId: string;
  onReturnStarted: () => void;
}) {
  const [openSaleId, setOpenSaleId] = useState<string | null>(null);
  const [openCardNumber, setOpenCardNumber] = useState<string | null>(null);

  const layaways = useQuery({
    queryKey: ['register', 'layaways', customerId],
    queryFn: () => api.register.layaways.query({ customerId, status: 'open' }),
  });

  const cards = useQuery({
    queryKey: ['register', 'giftCards', customerId],
    queryFn: () => api.register.giftCards.query({ customerId }),
  });

  const openLayaways = layaways.data ?? [];
  const liveCards = (cards.data ?? []).filter((card) => card.balanceCents > 0);

  if (openLayaways.length === 0 && liveCards.length === 0) return null;

  return (
    <Card title="Held for this customer">
      {openLayaways.length > 0 ? (
        <>
          <span className="label-micro">Layaways</span>
          <table className="table">
            <thead>
              <tr>
                <th>Started</th>
                <th>Due</th>
                <th className="num">Value</th>
                <th className="num">Outstanding</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {openLayaways.map((entry) => (
                <tr key={entry.id}>
                  <td className="text-secondary">
                    {new Date(entry.createdAt).toLocaleDateString()}
                  </td>
                  <td className="text-secondary">
                    {entry.dueDate === null ? '—' : new Date(entry.dueDate).toLocaleDateString()}
                  </td>
                  <td className="num money">{money(entry.totalCents)}</td>
                  <td className="num money">{money(entry.balanceCents)}</td>
                  <td className="num">
                    {/* Straight to the document a payment is taken on. */}
                    <Button variant="ghost" onClick={() => setOpenSaleId(entry.saleId)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {liveCards.length > 0 ? (
        <>
          <span className="label-micro">Gift cards</span>
          <table className="table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Bought</th>
                <th className="num">Remaining</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {liveCards.map((card) => (
                <tr key={card.id}>
                  <td>
                    <Mono>{card.number}</Mono>
                    {card.previousNumber === null ? null : (
                      <span className="text-secondary customers__card-note">
                        Was <Mono>{card.previousNumber}</Mono> before Shekko
                      </span>
                    )}
                  </td>
                  <td className="text-secondary">
                    {/* A carried-over card was bought before Shekko; the day it arrived is not when. */}
                    {card.migrated
                      ? 'Before Shekko'
                      : new Date(card.createdAt).toLocaleDateString()}
                  </td>
                  <td className="num money">{money(card.balanceCents)}</td>
                  <td className="num">
                    <Button variant="ghost" onClick={() => setOpenCardNumber(card.number)}>
                      History
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {openSaleId !== null ? (
        <ReceiptDialog
          saleId={openSaleId}
          onClose={() => setOpenSaleId(null)}
          onReturnStarted={() => {
            setOpenSaleId(null);
            onReturnStarted();
          }}
        />
      ) : null}

      {openCardNumber !== null ? (
        <GiftCardHistoryDialog number={openCardNumber} onClose={() => setOpenCardNumber(null)} />
      ) : null}
    </Card>
  );
}

/**
 * One gift card: what is left on it and everywhere it has been (SCOPE §8.4).
 *
 * The balance is a cached column, but the movements beside it are the ledger it
 * is derived from — so a customer disputing "I had $50 on this" is answered by
 * a list rather than by an assertion. Straight from `giftCardBalance`, which
 * already returned both and had no screen showing the second half.
 */
function GiftCardHistoryDialog({ number, onClose }: { number: string; onClose: () => void }) {
  const card = useQuery({
    queryKey: ['register', 'giftCardBalance', number],
    queryFn: () => api.register.giftCardBalance.query({ number }),
  });

  return (
    <Modal
      open
      title="Gift card"
      description={card.data == null ? undefined : `${money(card.data.balanceCents)} remaining`}
      onClose={onClose}
      primaryAction={
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {card.isError ? <Callout tone="danger">{errorMessage(card.error)}</Callout> : null}
      {card.data == null ? (
        <Loading label="Loading the card" />
      ) : (
        <>
          <div className="field">
            <span className="field__label">Number</span>
            <Mono>{card.data.number}</Mono>
          </div>

          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {card.data.movements.map((movement, index) => (
                <tr key={index}>
                  <td className="text-secondary">
                    {new Date(movement.occurredAt).toLocaleString()}
                  </td>
                  <td>
                    {MOVEMENT_LABELS[movement.reason] ?? movement.reason}
                    {movement.note === null ? null : (
                      <span className="text-secondary customers__card-note">{movement.note}</span>
                    )}
                  </td>
                  <td className="num money">{money(movement.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Modal>
  );
}

/** Why value moved on a card, in words rather than column spelling. */
const MOVEMENT_LABELS: Readonly<Record<string, string>> = {
  issue: 'Sold',
  load: 'Loaded',
  redeem: 'Spent',
  refund: 'Refunded onto it',
  adjustment: 'Adjusted',
  migration: 'Carried over from the old system',
};

// ---------------------------------------------------------------------------

/**
 * What they bought before Shekko — the old system's sales, brought over line
 * by line (decision 205). Its own card, said plainly, because these are not
 * Shekko receipts: nothing here opens a receipt or starts a return. Absent for
 * a customer with none.
 */
function PastPurchases({ customerId }: { customerId: string }) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 150);
    return () => clearTimeout(timer);
  }, [term]);

  const past = useQuery({
    queryKey: ['customers', 'pastPurchases', customerId, debounced],
    queryFn: () =>
      api.customers.pastPurchases.query({
        customerId,
        ...(debounced === '' ? {} : { term: debounced }),
      }),
  });

  if (past.data === undefined || past.data.lines === 0) return null;
  const { rows, receipts, netCents, firstDay, lastDay } = past.data;
  const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString();

  return (
    <Card title="Before Shekko" padded={false}>
      <p className="text-secondary history__summary">
        {receipts} {receipts === 1 ? 'visit' : 'visits'}, {money(netCents)} in the old system
        {firstDay !== null && lastDay !== null ? `, ${day(firstDay)} to ${day(lastDay)}` : ''}.
        Brought over for reference — these receipts cannot be returned against here.
      </p>
      <div className="history__filters">
        <input
          className="input"
          placeholder="Filter by item, SKU or old receipt number…"
          aria-label="Filter what they bought before Shekko"
          value={term}
          autoComplete="off"
          onChange={(event) => setTerm(event.target.value)}
        />
      </div>
      {rows.length === 0 ? (
        <p className="text-secondary history__empty">Nothing matches that.</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Item</th>
                <th>Old receipt</th>
                <th className="num">Qty</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((line) => (
                <tr key={line.id}>
                  <td className="text-secondary">{day(line.day)}</td>
                  <td>
                    {line.description}
                    <div className="history__meta">
                      <Mono>{line.sku ?? '—'}</Mono>
                      {line.quantity < 0 ? <Badge tone="danger">Returned</Badge> : null}
                      {line.online ? <Badge tone="info">Online</Badge> : null}
                    </div>
                  </td>
                  <td>
                    <Mono>{line.receipt}</Mono>
                  </td>
                  <td className="num">{Math.abs(line.quantity)}</td>
                  <td className="num money">{money(line.netCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

/**
 * Every item they have ever bought, filterable by date and by item.
 *
 * The client asked for this by name. It is also the fastest route to a return
 * they have no receipt for: find the person, find the jacket, open the receipt
 * it was on.
 */
function PurchaseHistory({
  customerId,
  onReturnStarted,
}: {
  customerId: string;
  onReturnStarted: () => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openSaleId, setOpenSaleId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 150);
    return () => clearTimeout(timer);
  }, [term]);

  const history = useQuery({
    queryKey: ['customers', 'history', customerId, debounced, from, to],
    queryFn: () =>
      api.customers.history.query({
        customerId,
        ...(debounced === '' ? {} : { term: debounced }),
        ...(from === '' ? {} : { from }),
        ...(to === '' ? {} : { to }),
      }),
  });

  const rows = history.data ?? [];
  const filtered = debounced !== '' || from !== '' || to !== '';

  return (
    <Card title="Purchase history" padded={false}>
      <div className="history__filters">
        <input
          className="input"
          placeholder="Filter by item, SKU or receipt…"
          value={term}
          autoComplete="off"
          onChange={(event) => setTerm(event.target.value)}
        />
        <label className="field__label" htmlFor="history-from">
          From
        </label>
        <DateInput
          id="history-from"

          value={from}
          onChange={(value) => setFrom(value)}
        />
        <label className="field__label" htmlFor="history-to">
          To
        </label>
        <DateInput
          id="history-to"

          value={to}
          onChange={(value) => setTo(value)}
        />
      </div>

      {history.isPending ? (
        <Loading label="Loading history" />
      ) : rows.length === 0 ? (
        <p className="text-secondary history__empty">
          {filtered ? 'Nothing matches those filters.' : 'No purchases on record yet.'}
        </p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Item</th>
                <th>Receipt</th>
                <th className="num">Qty</th>
                <th className="num">Each</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((line) => (
                <tr key={line.id}>
                  <td className="text-secondary">
                    {line.completedAt === null
                      ? '—'
                      : new Date(line.completedAt).toLocaleDateString()}
                  </td>
                  <td>
                    {line.description}
                    <div className="history__meta">
                      <Mono>{line.sku ?? '—'}</Mono>
                      {line.isRefund ? <Badge tone="danger">Returned</Badge> : null}
                      {!line.isRefund && line.refundedQuantity > 0 ? (
                        <Badge tone="warning">{line.refundedQuantity} came back</Badge>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setOpenSaleId(line.saleId)}
                    >
                      <Mono>{line.saleNumber ?? '—'}</Mono>
                    </button>
                  </td>
                  <td className="num">{Math.abs(line.quantity)}</td>
                  <td className="num money">{money(line.unitPriceCents)}</td>
                  <td className="num money">{money(line.netCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openSaleId !== null ? (
        <ReceiptDialog
          saleId={openSaleId}
          onClose={() => setOpenSaleId(null)}
          onReturnStarted={() => {
            setOpenSaleId(null);
            onReturnStarted();
          }}
        />
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function TagEditor({ customerId, tags }: { customerId: string; tags: string[] }) {
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['customers', 'get', customerId] });

  const add = useMutation({
    mutationFn: (tag: string) => api.customers.addTag.mutate({ customerId, tag }),
    onSuccess: () => void invalidate(),
  });

  const remove = useMutation({
    mutationFn: (tag: string) => api.customers.removeTag.mutate({ customerId, tag }),
    onSuccess: invalidate,
  });

  const suggestions = useQuery({
    queryKey: ['customers', 'tags'],
    queryFn: () => api.customers.tags.query(),
    staleTime: 300_000,
  });

  return (
    <div className="tags">
      {tags.map((tag) => (
        <span key={tag} className="tag">
          {tag}
          <button
            type="button"
            className="tag__remove"
            aria-label={`Remove the ${tag} tag`}
            disabled={remove.isPending}
            onClick={() => remove.mutate(tag)}
          >
            ×
          </button>
        </span>
      ))}

      {/* Recognition over recall (§2.2-3): the tags this shop already uses,
          in the store's own list — or a new one, by name (2026-10-16: the
          browser's suggestion list is not ours). */}
      <span className="tag__add">
        <SearchSelect
          aria-label="Add a tag"
          options={(suggestions.data ?? [])
            .filter((entry) => !tags.includes(entry.tag))
            .map((entry) => ({ value: entry.tag, label: entry.tag }))}
          value=""
          onChange={(tag) => {
            if (tag.trim() !== '') add.mutate(tag.trim());
          }}
          placeholder="Add a tag"
          emptyText="Type a new tag"
          newLabel={(typed) => `Add the tag “${typed}”`}
          maxLength={40}
          disabled={add.isPending}
        />
      </span>

      {add.isError ? <Callout tone="danger">{errorMessage(add.error)}</Callout> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Store credit: what is on the account, where it came from, and issuing it by
 * hand (revision item 4b).
 *
 * Until now there was exactly one way credit came into existence — refunding a
 * return to it — so goodwill after a bad fitting, a correction to a refund keyed
 * at the wrong figure, and eventually the payout consignment owes a customer
 * (item 38) all had **no path in the product at all**. The shop's only option
 * was to ask for a database edit.
 *
 * The ledger sits above the action deliberately. Adjusting a balance without
 * seeing what is already on it is how somebody issues a second $50 for the same
 * apology, and the movements answer the question the balance raises.
 */
function StoreCreditPanel({
  customerId,
  customerName,
  balanceCents,
  ledger,
}: {
  customerId: string;
  customerName: string;
  balanceCents: number;
  ledger: ReadonlyArray<{
    id: string;
    amountCents: number;
    reason: string;
    occurredAt: string | Date;
    note: string | null;
  }>;
}) {
  const [adjusting, setAdjusting] = useState(false);

  return (
    <div className="profile__admin">
      <p className="text-secondary">
        {balanceCents === 0
          ? 'Nothing on this account. Credit issued here is spendable at the register straight away.'
          : `${money(balanceCents)} on the account, spendable at the register like any other tender.`}
      </p>

      {ledger.length === 0 ? null : (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Why</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {ledger.map((movement) => (
              <tr key={movement.id}>
                <td className="text-secondary">
                  {new Date(movement.occurredAt).toLocaleDateString()}
                </td>
                <td>
                  {STORE_CREDIT_REASONS[movement.reason] ?? movement.reason}
                  {movement.note === null ? null : (
                    <span className="text-secondary"> · {movement.note}</span>
                  )}
                </td>
                {/* Signed, because "$50" tells you nothing about which way it
                    went and both directions are ordinary here. */}
                <td className="num money">
                  {movement.amountCents > 0 ? '+' : '−'}
                  {money(Math.abs(movement.amountCents))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Button variant="secondary" onClick={() => setAdjusting(true)}>
        Issue or adjust credit
      </Button>

      {adjusting ? (
        <StoreCreditDialog
          customerId={customerId}
          customerName={customerName}
          balanceCents={balanceCents}
          onClose={() => setAdjusting(false)}
        />
      ) : null}
    </div>
  );
}

/** The ledger's own vocabulary, in the words a counter uses. */
const STORE_CREDIT_REASONS: Record<string, string> = {
  refund: 'Refunded to credit',
  redemption: 'Spent on a sale',
  adjustment: 'Issued by hand',
  expiry: 'Expired',
  // A consigned item sold (paid) or came back on a return (taken back) — decision 131.
  consignment: 'Consignment',
};

function StoreCreditDialog({
  customerId,
  customerName,
  balanceCents,
  onClose,
}: {
  customerId: string;
  customerName: string;
  balanceCents: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [taking, setTaking] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);
  const nothingToTakeId = useId();

  const parsed = tryParseMoney(amount);
  /** Signed at the edge, so everything below it reasons about one number. */
  const amountCents = parsed === null ? null : taking ? -parsed : parsed;

  const save = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.customers.adjustStoreCredit.mutate({
        customerId,
        amountCents: amountCents!,
        note: note.trim(),
        ...(override === undefined ? {} : { override }),
      }),
    onSuccess: (result) => {
      recordBreadcrumb('customers', taking ? 'took back store credit' : 'issued store credit');
      notify({
        title: `${taking ? 'Store credit taken back' : 'Store credit added'} — ${money(
          Math.abs(amountCents ?? 0),
        )} for ${customerName}`,
        detail: `${money(result.balanceCents)} on the account now.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['customers', 'get', customerId] });
      onClose();
    },
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  if (needsApproval) {
    const refusal = save.isError ? approvalRefusal(save.error) : null;

    return (
      <ManagerApproval
        title="Manager approval needed"
        because={
          taking
            ? 'Taking back store credit reduces money the shop owes this customer.'
            : 'Issuing store credit is money the shop will owe this customer.'
        }
        busy={save.isPending}
        error={refusal}
        onCancel={onClose}
        onApprove={(override) => save.mutate(override)}
      />
    );
  }

  /*
   * Refused here as well as on the server, so the operator is told before the
   * press rather than after it (§2.2-7). The server is still the authority —
   * this is the client telling the truth early, never a second rule.
   */
  const overdrawn = amountCents !== null && amountCents < 0 && -amountCents > balanceCents;

  return (
    <Modal
      open
      title={taking ? 'Take back store credit' : 'Issue store credit'}
      description={
        taking
          ? `${money(balanceCents)} on the account. What comes off, and why.`
          : 'Money the shop will owe this customer, spendable at the register straight away.'
      }
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={
            parsed === null || parsed === 0
              ? 'Enter an amount'
              : overdrawn
                ? `There is only ${money(balanceCents)} to take back`
                : note.trim() === ''
                  ? 'Say why'
                  : undefined
          }
          onClick={() => save.mutate(undefined)}
        >
          {taking ? 'Take it back' : 'Issue credit'}
        </Button>
      }
    >
      {/*
        One field and a direction, rather than a signed amount. Typing "-40"
        into a money box is a convention nobody at a counter shares, and a
        minus sign lost to a stray keystroke is the difference between giving
        somebody $40 and taking it away.
      */}
      <div className="chips">
        <button
          type="button"
          className={`chip${taking ? '' : ' chip--on'}`}
          onClick={() => setTaking(false)}
        >
          Issue
        </button>
        <button
          type="button"
          className={`chip${taking ? ' chip--on' : ''}`}
          disabled={balanceCents <= 0}
          aria-describedby={balanceCents <= 0 ? nothingToTakeId : undefined}
          onClick={() => setTaking(true)}
        >
          Take back
        </button>
      </div>
      {/* A disabled control says why (DESIGN-DNA; the browser suite's check, 2026-10-27). */}
      {balanceCents <= 0 ? (
        <p className="text-secondary" id={nothingToTakeId}>
          No store credit on the account to take back.
        </p>
      ) : null}

      <input
        className="input"
        autoFocus
        inputMode="decimal"
        placeholder="0.00"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
      />

      {/*
        Required, not optional. This is the one movement kind with no document
        behind it — a refund has a sale, a redemption has a sale, and an
        adjustment has only what somebody typed. A $200 line with no reason is
        barely better than no line at all.
      */}
      <input
        className="input"
        maxLength={200}
        placeholder="Why — the customer sees none of this"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />

      {save.isError && !isOverridable(save.error) ? (
        <Callout tone="danger">{errorMessage(save.error)}</Callout>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function TaxExemptDialog({
  customerId,
  customerName,
  currentlyExempt,
  onClose,
}: {
  customerId: string;
  customerName: string;
  currentlyExempt: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [reference, setReference] = useState('');
  const [needsApproval, setNeedsApproval] = useState(false);

  const save = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.customers.setTaxExempt.mutate({
        customerId,
        taxExempt: !currentlyExempt,
        reference: currentlyExempt ? null : reference.trim(),
        ...(override === undefined ? {} : { override }),
      }),
    onSuccess: () => {
      recordBreadcrumb('customers', currentlyExempt ? 'removed tax exemption' : 'set tax exempt');
      notify({
        title: currentlyExempt
          ? `Tax exemption removed — ${customerName}`
          : `Tax exempt — ${customerName}`,
        detail: currentlyExempt
          ? 'Future sales to them are taxed normally.'
          : 'No sales tax on their future sales.',
      });
      void queryClient.invalidateQueries({ queryKey: ['customers', 'get', customerId] });
      onClose();
    },
    onError: (error) => {
      // The server tags a refusal that a manager PIN would unlock. Offering the
      // prompt is the difference between a guardrail and a roadblock (§2-6).
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  if (needsApproval) {
    // Once the prompt is up, every refusal has to say something — see
    // `approvalRefusal`. Silence leaves a manager pressing Approve and watching
    // nothing happen.
    const refusal = save.isError ? approvalRefusal(save.error) : null;

    return (
      <ManagerApproval
        title="Manager approval needed"
        because={
          currentlyExempt
            ? 'Removing a tax exemption changes what this customer is charged.'
            : 'A tax exemption stops sales tax being charged on every future sale.'
        }
        busy={save.isPending}
        error={refusal}
        onCancel={onClose}
        onApprove={(override) => save.mutate(override)}
      />
    );
  }

  return (
    <Modal
      open
      title={currentlyExempt ? 'Remove tax exemption?' : 'Mark tax exempt'}
      description={
        currentlyExempt
          ? 'Future sales to this customer will be taxed normally.'
          : 'Record the resale certificate or exemption reference. It stays on the customer record.'
      }
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={
            currentlyExempt || reference.trim() !== ''
              ? undefined
              : 'Enter the certificate or exemption reference'
          }
          onClick={() => save.mutate(undefined)}
        >
          {currentlyExempt ? 'Remove exemption' : 'Mark tax exempt'}
        </Button>
      }
    >
      {currentlyExempt ? null : (
        <input
          className="input"
          autoFocus
          maxLength={120}
          placeholder="Resale certificate number"
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
      )}
      {save.isError && !isOverridable(save.error) ? (
        <Callout tone="danger">{errorMessage(save.error)}</Callout>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

/**
 * The full record.
 *
 * Everything the quick add left out lives here, and address and notes sit
 * behind a "More" so the common edit — a changed phone number — is not buried
 * under nine fields (§2.2-2).
 */
function EditCustomerDialog({
  customer,
  onClose,
}: {
  customer: Profile['customer'];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [draft, setDraft] = useState(() => ({
    firstName: customer.firstName ?? '',
    lastName: customer.lastName ?? '',
    companyName: customer.companyName ?? '',
    phone: customer.phone ?? '',
    altPhone: customer.altPhone ?? '',
    email: customer.email ?? '',
    addressLine1: customer.addressLine1 ?? '',
    addressLine2: customer.addressLine2 ?? '',
    city: customer.city ?? '',
    region: customer.region ?? '',
    postalCode: customer.postalCode ?? '',
    birthday: customer.birthday ?? '',
    notes: customer.notes ?? '',
    marketingOptIn: customer.marketingOptIn,
  }));

  // Only a changed email or phone is news (FB-1104): the record's own details
  // are not a duplicate of themselves.
  const changedContact =
    draft.email.trim().toLowerCase() !== (customer.email ?? '') ||
    draft.phone.trim() !== (customer.phone ?? '');
  const found = useDuplicates(
    changedContact ? draft.email : '',
    changedContact ? draft.phone : '',
    customer.id,
  );
  const sharesEmail =
    found.sharesEmail && draft.email.trim().toLowerCase() !== (customer.email ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.customers.update.mutate({
        customerId: customer.id,
        ...draft,
        // A blank date input is "not set", not an invalid date.
        birthday: draft.birthday === '' ? null : draft.birthday,
        ...(sharesEmail ? { confirmDuplicate: true } : {}),
      }),
    onSuccess: () => {
      notify({ title: `Saved — ${customerName(draft)}` });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      onClose();
    },
  });

  const field = (key: keyof typeof draft & string, label: string, type = 'text') => (
    <div className="field" key={key}>
      <label className="field__label" htmlFor={`edit-${key}`}>
        {label}
      </label>
      {/* A date is typed in the box and picked from the store's own calendar. */}
      {type === 'date' ? (
        <DateInput
          id={`edit-${key}`}
          value={String(draft[key] ?? '')}
          onChange={(value) => setDraft((current) => ({ ...current, [key]: value }))}
        />
      ) : (
        <input
          id={`edit-${key}`}
          className="input"
          type={type}
          autoComplete="off"
          value={String(draft[key] ?? '')}
          onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}
        />
      )}
    </div>
  );

  const identifiable = [
    draft.firstName,
    draft.lastName,
    draft.companyName,
    draft.phone,
    draft.email,
  ]
    .join('')
    .trim();

  return (
    <Modal
      open
      wide
      title={`Edit ${customerName(customer)}`}
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={identifiable === '' ? 'Keep at least a name, phone or email' : undefined}
          onClick={() => save.mutate()}
        >
          {sharesEmail ? 'Save anyway — someone else' : 'Save'}
        </Button>
      }
    >
      <DuplicateNotice duplicates={changedContact ? found.duplicates : []} />
      <div className="quick-add">
        {field('firstName', 'First name')}
        {field('lastName', 'Last name')}
        {/* The shared fields, so editing a customer punctuates and completes
            exactly the way creating one does. */}
        <PhoneField
          value={draft.phone}
          onChange={(value) => setDraft((current) => ({ ...current, phone: value }))}
        />
        <EmailField
          value={draft.email}
          onChange={(value) => setDraft((current) => ({ ...current, email: value }))}
        />
      </div>

      <label className="toggle">
        <input
          type="checkbox"
          checked={draft.marketingOptIn}
          onChange={(event) =>
            setDraft((current) => ({ ...current, marketingOptIn: event.target.checked }))
          }
        />
        <span>Happy to receive marketing email</span>
      </label>

      <More label="Company, address, birthday and notes">
        <div className="quick-add">
          {field('companyName', 'Company')}
          {field('altPhone', 'Other phone')}
          {field('addressLine1', 'Address')}
          {field('addressLine2', 'Address line 2')}
          {field('city', 'City')}
          {field('region', 'State')}
          {field('postalCode', 'ZIP')}
          {field('birthday', 'Birthday', 'date')}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="edit-notes">
            Notes
          </label>
          <textarea
            id="edit-notes"
            className="input textarea"
            rows={3}
            placeholder="Boot-fit specs, sizing preferences, anything staff need at the counter."
            value={draft.notes}
            onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))}
          />
        </div>
      </More>

      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}
    </Modal>
  );
}

/**
 * Layaways that are over — paid off or canceled (FB-1088).
 *
 * A canceled layaway's sale is voided, and every history leaves voided sales
 * out, so it vanished from the customer: the deposit, the fee kept and the
 * money given back could be found only in End of Day or Activity. "What
 * happened to my layaway?" is asked here, so it is answered here. Open ones are
 * in *Held for this customer*, where a payment is taken; this card is only the
 * record, and is absent when there is none (§2.2-13).
 */
function PastLayaways({
  customerId,
  onReturnStarted,
}: {
  customerId: string;
  onReturnStarted: () => void;
}) {
  const [openSaleId, setOpenSaleId] = useState<string | null>(null);
  const layaways = useQuery({
    queryKey: ['register', 'layaways', customerId, 'all'],
    queryFn: () => api.register.layaways.query({ customerId }),
  });
  const past = (layaways.data ?? []).filter((entry) => entry.status !== 'open');
  if (past.length === 0) return null;

  return (
    <Card title="Past layaways" padded={false}>
      <table className="table">
        <thead>
          <tr>
            <th>Started</th>
            <th>Ended</th>
            <th>How</th>
            <th className="num">Paid</th>
            <th className="num">Fee kept</th>
            <th className="num">Given back</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {past.map((entry) => {
            const ended = entry.status === 'cancelled' ? entry.cancelledAt : entry.completedAt;
            return (
              <tr key={entry.id}>
                <td className="text-secondary">{new Date(entry.createdAt).toLocaleDateString()}</td>
                <td className="text-secondary">
                  {ended === null ? '—' : new Date(ended).toLocaleDateString()}
                </td>
                <td>
                  <Badge tone={entry.status === 'cancelled' ? 'neutral' : 'success'}>
                    {entry.status === 'cancelled' ? 'Canceled' : 'Paid off'}
                  </Badge>
                </td>
                <td className="num money">{money(entry.paidCents)}</td>
                <td className="num money">{money(entry.feeCents)}</td>
                <td className="num money">{money(entry.refundedCents)}</td>
                <td className="num">
                  <Button variant="ghost" onClick={() => setOpenSaleId(entry.saleId)}>
                    Open
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {openSaleId !== null ? (
        <ReceiptDialog
          saleId={openSaleId}
          onClose={() => setOpenSaleId(null)}
          onReturnStarted={() => {
            setOpenSaleId(null);
            onReturnStarted();
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * The customer's repairs (SCOPE §9.1, decision 127): what is in the shop for
 * them now, and what has been done before. Shown only when there is something
 * to show (§2.2-13).
 */
function CustomerRepairs({ customerId }: { customerId: string }) {
  const tickets = useQuery({
    queryKey: ['repairs', 'forCustomer', customerId],
    queryFn: () => api.repairs.forCustomer.query({ customerId }),
  });
  const rows = tickets.data ?? [];
  if (rows.length === 0) return null;

  const words: Record<string, string> = {
    intake: 'taken in',
    in_progress: 'in progress',
    ready: 'ready for pickup',
    picked_up: 'picked up',
    cancelled: 'canceled',
  };

  return (
    <Card title="Repairs" padded={false}>
      <table className="table">
        <thead>
          <tr>
            <th>Ticket</th>
            <th>Equipment</th>
            <th>Status</th>
            <th>Taken in</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((ticket) => (
            <tr key={ticket.id}>
              <td>
                <Mono>{ticket.number}</Mono>
              </td>
              <td>{ticket.equipment}</td>
              <td>
                <Badge tone={ticket.status === 'ready' ? 'success' : 'neutral'}>
                  {words[ticket.status] ?? ticket.status}
                </Badge>
                {ticket.status === 'cancelled' && ticket.cancelReason ? (
                  <div className="text-secondary">{ticket.cancelReason}</div>
                ) : null}
              </td>
              <td className="text-secondary">{new Date(ticket.createdAt).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
